import { DatePicker } from '@data/components/report/ReportFilterBar/dropdowns/DateRangeDropdown/DatePicker/DatePicker';
import { Select, SelectItem } from '@scholastic/mockingjay';
import {
  differenceInCalendarDays,
  endOfMonth,
  format,
  startOfMonth,
  subDays,
  subMonths,
} from 'date-fns';

import { useCallback, useMemo, useRef, useState } from 'react';
import { getSchoolYearDateRange } from '@data/utils/get-school-year';

interface DateRangeOption {
  value: string;
  label: string;
}

const MONTH_KEY_FORMAT = 'yyyy-MM';
const MONTH_LABEL_FORMAT = 'MMMM yyyy';
const MONTH_VALUE_PREFIX = 'month-';

const LAST_7_VALUE = 'last7days';
const LAST_30_VALUE = 'last30days';
const CUSTOM_RANGE = 'custom-range';

export const SCHOOL_YEAR_VALUE = 'school-year';

export interface DateRange {
  to: Date;
  from: Date;
}

function buildMonthOption(monthDate: Date): DateRangeOption {
  return {
    value: `${MONTH_VALUE_PREFIX}${format(monthDate, MONTH_KEY_FORMAT)}`,
    label: format(monthDate, MONTH_LABEL_FORMAT),
  };
}

function buildDateOptions(): DateRangeOption[] {
  const { from: schoolYearStart, to: today } = getSchoolYearDateRange();
  const daysSinceStart = differenceInCalendarDays(today, schoolYearStart);

  const options: DateRangeOption[] = [
    { value: SCHOOL_YEAR_VALUE, label: 'Current school year' },
  ];

  if (daysSinceStart >= 7) {
    options.push({ value: LAST_7_VALUE, label: 'Last 7 days' });
  }
  if (daysSinceStart >= 30) {
    options.push({ value: LAST_30_VALUE, label: 'Last 30 days' });
  }

  // Show up to 3 months prior to the current month, newest first.
  // Do not show months earlier than the school year start month, July.
  for (let i = 0; i < 3; i += 1) {
    const monthDate = subMonths(startOfMonth(today), i);
    if (monthDate < startOfMonth(schoolYearStart)) break;
    options.push(buildMonthOption(monthDate));
  }

  options.push({ value: CUSTOM_RANGE, label: 'Custom range' });

  return options;
}

function dateRangeFromOption(option: string): DateRange {
  const today = new Date();

  if (option === SCHOOL_YEAR_VALUE) {
    return getSchoolYearDateRange();
  }
  if (option === LAST_7_VALUE) return { from: subDays(today, 7), to: today };
  if (option === LAST_30_VALUE) return { from: subDays(today, 30), to: today };

  if (option.startsWith(MONTH_VALUE_PREFIX)) {
    const parts = option.split('-');
    const year = parseInt(parts[1] ?? '', 10);
    const month = parseInt(parts[2] ?? '', 10);
    if (!Number.isNaN(year) && !Number.isNaN(month)) {
      const monthDate = new Date(year, month - 1, 1);
      const { from: schoolYearStart } = getSchoolYearDateRange();
      const monthStart = startOfMonth(monthDate);
      // Never start earlier than the school year start (e.g. July starts 7/15).
      const from = monthStart < schoolYearStart ? schoolYearStart : monthStart;
      const isCurrentMonth =
        format(today, MONTH_KEY_FORMAT) === `${parts[1]}-${parts[2]}`;
      const to = isCurrentMonth ? today : endOfMonth(monthDate);
      return { from, to };
    }
  }

  return { from: subDays(today, 7), to: today };
}

export interface DateRangeDropdownProps {
  useMaxWidth?: boolean;
  onChange?: (range: DateRange, optionValue: string) => void;
  // When a date range is carried in on the URL (e.g. after a drill-down), the
  // dropdown defaults to "Custom Range" and seeds the picker with that range.
  initialCustomRange?: DateRange | null;
}

export function DateRangeDropdown({
  useMaxWidth = false,
  onChange,
  initialCustomRange = null,
}: DateRangeDropdownProps) {
  const dateOptions = useMemo(() => buildDateOptions(), []);
  const initialOption = initialCustomRange ? CUSTOM_RANGE : SCHOOL_YEAR_VALUE;
  const [selectedOption, setSelectedOption] = useState(initialOption);
  const [selectOpen, setSelectOpen] = useState(false);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<DateRange | null>(
    initialCustomRange
  );

  const pendingValueRef = useRef<string | null>(null);
  // The last option that was actually committed, so we can revert to it if the
  // user opens the custom range picker but dismisses it without clicking "Done".
  const committedOptionRef = useRef<string>(initialOption);

  const handleChange = useCallback(
    (value: string) => {
      pendingValueRef.current = value;
      setSelectedOption(value);

      if (value === CUSTOM_RANGE) {
        setShowCustomDatePicker(true);
      } else {
        setShowCustomDatePicker(false);
        committedOptionRef.current = value;
        onChange?.(dateRangeFromOption(value), value);
      }
    },
    [onChange]
  );

  const handleSetCustomDateRange = useCallback(
    (range: DateRange) => {
      setCustomDateRange(range);
      committedOptionRef.current = CUSTOM_RANGE;
      onChange?.(range, CUSTOM_RANGE);
      setShowCustomDatePicker(false);
    },
    [onChange]
  );

  // Called when the custom date picker is dismissed (X, Cancel, or click-out).
  // Revert the dropdown label to the last committed option. If the user clicked
  // "Done", the committed option is already CUSTOM_RANGE so the label stays.
  const handleCloseCustomDatePicker = useCallback(() => {
    setShowCustomDatePicker(false);
    setSelectedOption(committedOptionRef.current);
  }, []);

  return (
    <div
      className={`${
        useMaxWidth ? 'w-64' : 'w-full'
      } relative flex [&>div]:w-full [&_button]:min-w-0`}
    >
      <Select
        label="Date Range"
        value={selectedOption}
        onValueChange={handleChange}
        open={selectOpen}
        onOpenChange={(open) => {
          setSelectOpen(open);
          if (!open) {
            const closingValue = pendingValueRef.current ?? selectedOption;
            if (closingValue === CUSTOM_RANGE) {
              setShowCustomDatePicker(true);
            }
            pendingValueRef.current = null;
          }
        }}
      >
        {dateOptions.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </Select>

      {showCustomDatePicker && (
        <DatePicker
          customDateRange={customDateRange}
          setCustomDateRange={(dates: DateRange) =>
            handleSetCustomDateRange(dates)
          }
          setShowCustomDatePicker={(value: boolean) => {
            if (!value) {
              handleCloseCustomDatePicker();
            } else {
              setShowCustomDatePicker(true);
            }
          }}
        />
      )}
    </div>
  );
}
